import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import toConnectedMapNode from 'common/src/modules/campaigns/store/campaignLogicTab/utils/toConnectedMapNode';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../../campaignSetupAgent.enums';
import {cardToText, findFallbackCreativeNodeId} from '../../cardDetailsV2/textualConverters';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';

const getCardDetailsFromReduxToolSchema = z.object({
  nodeIds: z.array(z.string().describe(describeIdFormat(IdPrefix.Card))),
});

export function getCardDetailsFromReduxTool(store: CLStore) {
  async function execute({
    input: {nodeIds},
  }: ToolExecuteParams<typeof getCardDetailsFromReduxToolSchema>): Promise<ToolResult> {
    try {
      const state = store.getState();
      const {campaignLogic} = state.campaignPage;

      const nodes = campaignLogic.nodes[CampaignTreeType.Campaign];
      const fallbackCreativeNodeId = findFallbackCreativeNodeId(nodes);

      const numericIds: number[] = [];

      for (const rawId of nodeIds) {
        const result = idHelper.parseCardId(rawId);

        if (result.ok === false) {
          return toolError(formatParseError(result.error));
        }

        numericIds.push(result.numericId);
      }

      const existingNodes = numericIds.map((nodeId) => nodes[nodeId]).filter(Boolean);

      const textResults = existingNodes.map((node) => {
        const connectedNode = toConnectedMapNode(node);
        const text = cardToText(connectedNode, 'full', fallbackCreativeNodeId);

        return {node, text};
      });

      const cardTexts = textResults
        .filter((result): result is {node: typeof result.node; text: string} => result.text != null)
        .map((result) => result.text);

      const unsupportedCardIds = textResults
        .filter((result) => result.text == null)
        .map((result) => idHelper.toCardId(result.node.id));

      const missingNodesMessage =
        existingNodes.length !== numericIds.length
          ? `Some cards were not found: ${JSON.stringify(
              numericIds
                .filter((nodeId) => !existingNodes.some((node) => node.id === nodeId))
                .map((id) => idHelper.toCardId(id)),
            )}`
          : '';

      const unsupportedMessage =
        unsupportedCardIds.length === 1
          ? `Card ${unsupportedCardIds[0]} is of a card type that is currently not supported.`
          : unsupportedCardIds.length > 1
            ? `Cards ${unsupportedCardIds.join(', ')} are of card types that are currently not supported.`
            : '';

      const body = cardTexts.length > 0 ? cardTexts.join('\n\n') : '';
      const messages = [body, missingNodesMessage, unsupportedMessage].filter(Boolean);

      return toolResult(messages.join('\n\n'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      return toolError(`Error retrieving card details: ${errorMessage}`);
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.GetCardDetailsFromRedux,
    displayName: 'Retrieving Card Details',
    description:
      'Retrieves full internal configuration of cards. Only use when the needed details are not already available in the context.',
    execute,
    inputSchema: getCardDetailsFromReduxToolSchema,
    isReadonly: true,
  });
}
